import { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDebounce } from "use-debounce";
import { useAuth0 } from "@auth0/auth0-react";
import { useHistory, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { format } from "date-fns";

import ReactTable from "components/lib/Global/ReactTable";
import Sidebar from "components/Applications/Sidebar";
import HeroSection from "components/Applications/HeroSection";
import OrgAdminDashboardWrapper from "components/lib/Global/OrgAdminDashboardWrapper";
import SearchInput from "components/lib/Shared/SearchInput";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import SubRowAsync from "./SubRow";
import { Constants } from "config/constants";
import Checkbox from "components/lib/Shared/Checkbox";
import GoBackToHomeBtn from "../GoBackToHomeBtn";
import { getCompanyNameWithSuffix } from "utils/company";
import { getTransactionListAsync, getTransactionUnderReviewAsync } from "state/slices/transaction";
import { getMaintainYourCompanyReviewStatus } from "config/documentData";
import { getCompaniesAsync } from "state/slices/companies";
import { getCompaniesTransactionsListAsync } from "state/slices/companiesTransactions";
import HistoryTransactionReview from "components/HistoryTransactionReview";
import { getComments } from "state/slices/comment";
import { getCompany } from "state/slices/company";
import { XIcon } from "@heroicons/react/outline";
import { ChevronLeftIcon, ChevronRightIcon } from "components/lib/Shared/Icons/sflIcons";


const RenderHistoryTransactionReview = () => {
  const { transactionUnderReview, getTransactionUnderReview } = useSelector(
    (state) => state.transactionSlice
  )

  if (
    getTransactionUnderReview?.status === "loading") {
    return (
      <div className="flex justify-center mx-auto my-4">
        <SpinnerIcon className="text-gray-400" />
      </div>
    );
  }
  else if (transactionUnderReview?.items?.transactions?.length > 0) {
    return (
      <>
        <HistoryTransactionReview isFromAdmin={true} handleBackBtn={() => setShowHistoryView(false)} />
      </>

    )
  }

  return (
    <div className="flex justify-center mx-auto my-4">
      <SpinnerIcon className="text-gray-400" />
    </div>
  );
}

export default function Pending() {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useAuth0();
  const location = useLocation();
  const history = useHistory();
  const role = Cookies.get("role");
  const [statuses, setStatuses] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const { getUserStatus } = useSelector((state) => state.authSlice);
  const { getCompaniesTransactionsList, companiesTransactionsList } =
    useSelector((state) => state.companiesTransactionsSlice);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(() =>
    statuses.map((status) => status.value)
  );
  const { orgAdmin } = useSelector((state) => state.roleswitchSlice);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 1000);
  const [showHistoryView, setShowHistoryView] = useState(false);

  /**Use Effects */

  const handleHistoryView = (row) => {
    setShowHistoryView(true);
    dispatch(getTransactionUnderReviewAsync({ trxId: row.original._id }));
    dispatch(getComments({ companyId: row.original.companyInfo._id }));
    dispatch(getCompany({ id: row.original.companyInfo._id }));
  }

  useEffect(() => {
    if (role === Constants.COOKIE_ROLES.ORG_ADMIN) {
      setStatuses([
        { value: "Admin Review", label: "Admin Review" },
        { value: "Admin Review II", label: "Admin Review II" },
        { value: "Admin Corrections", label: "Admin Corrections" },
      ]);
    } else {
      setStatuses([
        { value: "Submission", label: "Submission" },
        { value: "Submission II", label: "Submission II" },
        { value: "Submission Corrections", label: "Submission Corrections" },
      ]);
    }
    setIsMounted(true);
  }, [role]);

  useEffect(() => {
    if (isAuthenticated) {
      const queryParams = new URLSearchParams(location.search);
      if (debouncedSearchTerm) setCurrentPage(1);
      const status = queryParams.getAll("status");
      const finalStatuses = status.flatMap((s) => {
        if (s === "BRS Review") {
          return ["BRS Review", "BRS-REVIEW"];
        }
        if (s === "Admin Review") {
          return [
            "Admin Review",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW,
          ];
        }
        if (s === "Admin Review II") {
          return [
            "Admin Review II",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW_II,
          ];
        }
        if (s === "Admin Corrections") {
          return [
            "Admin Corrections",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_CORRECTIONS,
          ];
        }
        if (s === "Admin Approval") {
          return [
            "Admin Approval",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_APPROVAL,
          ];
        }
        if (s === "Submission") {
          return [
            "Submission",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION,
          ];
        }
        if (s === "Submission Corrections") {
          return [
            "Submission Corrections",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION_CORRECTIONS,
          ];
        }
        if (s === "Submission II") {
          return [
            "Submission II",
            Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.SUBMISSION_II,
          ];
        }
        return s;
      });

      setFilters(finalStatuses);
      dispatch(
        getCompaniesTransactionsListAsync({
          queryParams: {
            limit: rowsPerPage,
            skip: (currentPage - 1) * rowsPerPage,
            search: debouncedSearchTerm,
            status: finalStatuses.length > 0 ? finalStatuses : null,
            order: "asc",
            orgAdmin: orgAdmin ? orgAdmin : "",
          },
        })
      );
    }
  }, [
    isAuthenticated,
    rowsPerPage,
    currentPage,
    debouncedSearchTerm,
    statuses,
    isMounted,
    location.search,
  ]);

  const currentUser = useSelector((state) => state.userSlice);

  const refreshTableData = async () => {
    let statuses = [];
    if (role === Constants.COOKIE_ROLES.ORG_ADMIN) {
      statuses = [
        "Admin Review",
        "Admin Corrections",
        "Admin Review II",
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW,
        Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.ADMIN_REVIEW_II,
      ];
    } else {
      statuses = ["Submission", "Submission Corrections", "Submission II"];
    }
    dispatch(
      getCompaniesTransactionsListAsync({
        queryParams: {
          limit: rowsPerPage,
          skip: (currentPage - 1) * rowsPerPage,
          search: debouncedSearchTerm,
          status: statuses.length > 0 ? statuses : null,
          order: "asc",
          orgAdmin: orgAdmin ? orgAdmin : "",
        },
      })
    );
  };

  // this function will render our row sub components
  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => {
      return (
        <SubRowAsync
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          refreshTableData={refreshTableData}
        />
      );
    },
    []
  );

  if (!currentUser?._id || getUserStatus.status === "loading") {
    return (
      <OrgAdminDashboardWrapper>
        <div className="flex justify-center mx-auto mt-10 text-white">
          <SpinnerIcon className="text-gray-400" />
        </div>
      </OrgAdminDashboardWrapper>
    );
  }
  const columns = [
    {
      Header: "Batch No.",
      accessor: "BatchNo",
      Cell: ({ row }) => row.original?.batchNo || "-",
    },
    {
      Header: "Name.",
      accessor: "companyName1",
      Cell: ({ row }) => {
        if (row.original?.isOrgAdmin) {
          return row.original?.names?.companyName;
        }
        if (row.original?.companyInfo) {
          return getCompanyNameWithSuffix(row.original.companyInfo);
        } else {
          return getCompanyNameWithSuffix(row.original);
        }
      },
    },
    {
      Header: "Application Type",
      accessor: "applicationType",

      Cell: ({ row }) => {
        if (row.original?.applicationType) {
          return row.original?.applicationType;
        } else {
          if (row.original?.transferYourBusinessInitiator) {
            return "Transfer Your Business";
          }
          if (row?.original?.isOrgAdmin) {
            return "Org Admin";
          }
          return "Incorporation";
        }
      },
    },
    {
      Header: "Change Type",
      accessor: "changeType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        };

        return (
          capitalize(original?.changeType) || original?.companyInfo?.type || "-"
        );
      },
    },
    {
      Header: "Transaction Type",
      accessor: "transactionType",
      Cell: ({ row: { original } }) => {
        const capitalize = (str) => {
          if (!str) return null;
          let transactionType =
            str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
          return transactionType;
        };

        return capitalize(original?.transactionType) || "Automatic";
      },
    },
    {
      Header: "Tnx No.",
      accessor: "TnxNo",
      Cell: ({ row: { original } }) => {
        return (
          original?.trxRef || original?.companyInfo?.registrationNumber || "-"
        );
      },
    },
    {
      Header: "Date",
      Cell: ({ row: { original } }) => {
        const date = original?.createdAt;
        return date ? format(new Date(date), "yyyy/MM/dd") : "N/A";
      },
    },
    {
      Header: "Status",
      Cell: ({ row }) => {
        if (row.original?.applicationType) {
          return getMaintainYourCompanyReviewStatus(row.original?.status);
        } else {
          if (row.original.status.includes("II")) {
            return (
              <>
                {row.original.status}{" "}
                <span className="items-center gap-x-1.5 rounded-full px-2 py-1 text-xxs font-medium text-gray-900 ring-1 ring-inset ring-green-primary bg-green-100/25 tracking-wide">
                  Corrected
                </span>
              </>
            );
          }
          if (
            ["Admin Review", "Submission", "BRS Review"].includes(
              row.original.status
            )
          ) {
            return (
              <>
                {row.original.status}{" "}
                <span className="items-center gap-x-1.5 rounded-full px-2 py-1 text-xxs font-medium text-gray-900 ring-1 ring-inset ring-primary-200 bg-purple-100/25 tracking-wide">
                  New
                </span>
              </>
            );
          }
          return row.original.status;
        }
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        if (row.original?.isForHistory) {
          return (
            <button
              type="button"
              onClick={() => handleHistoryView(row)}
              className={`text-gray-900 group flex rounded-md items-center w-full px-2 py-1 text-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500`}
            >
              {"View"}
            </button>
          )
        } else {
          return (
            <button
              type="button"
              {...row?.getToggleRowExpandedProps()}
              className={`text-gray-900 group flex rounded-md items-center w-full px-2 py-1 text-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500`}
            >
              {row.isExpanded ? "Close" : "View"}
            </button>
          );
        }

      },
    },
  ];

  function handleSearch(event) {
    setSearchTerm(event.target.value);
  }

  const handleFilterChange = (e) => {
    const params = new URLSearchParams();
    if (e.target.checked) {
      const tempFilters = [...filters, e.target.value];
      tempFilters.forEach((status) => params.append("status", status));
      history.push({ search: params.toString() });
      setFilters([...filters, e.target.value]);
    } else {
      const tempFilters = filters.filter((filter) => filter !== e.target.value);
      tempFilters.forEach((status) => params.append("status", status));
      history.push({ search: params.toString() });
      setFilters(tempFilters);
    }
    setCurrentPage(1);
  };




  const HistoryView = () => {
    return (
      <div className="col-span-6 pt-8 md:border-l md:col-span-5 md:pl-8 border-gray">

        <div className="flex justify-end items-end">
          <button
            type="button"
            onClick={() => setShowHistoryView(false)}

            className={`text-gray-900 group  flex rounded-md items-center px-2 py-1 text-sm disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 mb-4`}
          >

            <ChevronLeftIcon className="h-5 w-5 rotate-180 text-gray-400" aria-hidden="true" />
            Back to List
          </button>
        </div>
        <RenderHistoryTransactionReview />
      </div>
    )
  }

  const MainView = () => {
    return (
      <div className="col-span-6 pt-8 md:border-l md:col-span-5 md:pl-8 border-gray">
        <div className="mb-6">
          <h1 className="text-title5">Pending Applications</h1>
        </div>
        <div className="flex items-center justify-between my-2">
          <div className="flex items-center space-x-4">
            <h2 className="font-semibold">Filter by:</h2>
            <div className="flex my-2 space-x-2">
              {statuses.map((status) => (
                <Checkbox
                  key={status.value}
                  name={status.label}
                  label={status.label}
                  checked={filters.includes(status.value)}
                  onChange={handleFilterChange}
                />
              ))}
            </div>
          </div>
          <SearchInput value={searchTerm} handleOnChange={handleSearch} />
        </div>
        {companiesTransactionsList.items.length > 0 ? (
          <ReactTable
            columns={columns}
            data={companiesTransactionsList.items}
            totalRows={companiesTransactionsList.totalCount}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
            setCurrentPage={setCurrentPage}
            paginate
            renderRowSubComponent={renderRowSubComponent}
            isLoading={getCompaniesTransactionsList.status === "loading"}
          />
        ) : (
          <div className="flex items-center justify-center mt-10">
            {getCompaniesTransactionsList.status === "loading" ? (
              <SpinnerIcon className="text-gray-400" />
            ) : (
              <p className="text-2xl text-center text-primary">
                No applications available.
              </p>
            )}
          </div>
        )}
        {!getCompaniesTransactionsList.status === "loading" && (
          <div className="flex items-center justify-center mt-10">
            <p className="text-2xl text-center text-primary">
              No applications available.
            </p>
          </div>
        )}
      </div>
    )
  }

  return (
    <OrgAdminDashboardWrapper>
      <div className="grid gap-4 py-10 mx-auto">
        <HeroSection />
        <GoBackToHomeBtn />
        <div className="grid grid-cols-6 border-t border-gray">
          <div className="col-span-6 pr-8 md:col-span-1">
            <Sidebar />
          </div>
          {showHistoryView ? (
            <HistoryView />
          ) : <MainView />}
        </div>
      </div>
    </OrgAdminDashboardWrapper>
  );
}
